"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { capture } from "@/lib/analytics/posthog";
import type { PaidPlan } from "@/lib/billing/planConfig";

type PricingModalContextValue = {
  isOpen: boolean;
  /** True from the first open on — lets the lazy modal mount once and keep its close animation. */
  everOpened: boolean;
  /** Plan to ring in the modal (e.g. Studio when opened from an Audience gate). */
  highlight: PaidPlan | null;
  /** The surface that opened it — picks the modal's headline (pricingHeadline.ts). */
  source: string | null;
  /**
   * `source` names the surface that asked for the modal (a paywall, the nav,
   * a lapsed-subscription notice). It is optional so existing call-sites keep
   * working; they report as "unknown" until they pass one.
   */
  open: (source?: string, opts?: { highlight?: PaidPlan }) => void;
  close: () => void;
};

const PricingModalContext = createContext<PricingModalContextValue | null>(null);

export function PricingModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState<PaidPlan | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [everOpened, setEverOpened] = useState(false);

  const open = useCallback((rawSource?: string, opts?: { highlight?: PaidPlan }) => {
    // Wired as `onClick={open}` somewhere, the MouseEvent would arrive here.
    const from = typeof rawSource === "string" && rawSource ? rawSource : "unknown";
    setHighlight(opts?.highlight ?? null);
    setSource(from);
    setEverOpened(true);
    setIsOpen(true);
    capture("pricing modal opened", { source: from });
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, everOpened, highlight, source, open, close }),
    [isOpen, everOpened, highlight, source, open, close],
  );

  return (
    <PricingModalContext.Provider value={value}>
      {children}
    </PricingModalContext.Provider>
  );
}

export function usePricingModal(): PricingModalContextValue {
  const ctx = useContext(PricingModalContext);
  if (!ctx) {
    throw new Error("usePricingModal must be used within PricingModalProvider");
  }
  return ctx;
}
